import APICallWrapper, {APIError, APIResonse} from './APIWrapper.js';

/*
 * Module to provide access to the IPRS Web API
 */

const HTTP_POST         = 'POST'
//const HTTP_PUT          = 'PUT'

    
/**
 * This class provides an interface to the IPRS API. Each API call is wrapped 
 * by a function here. All these functions are asynchronous and return promises.
 * A typical call - using AppointmentTypeList as an example - looks like:-
 * ```
 * let api = new API()
 * api.AppointmentTypeList()
 * .then( response => response.apiResult ) // the response has fields like apiMetadata
 * .then( apiResult => {
 *      // Do something with the apiResult which is an array of objects with 
 *      // keys specific to the API call. In this case Title, Description
 * } )
 * .catch( error => { 
 *      // Do something with the error. This is a regular Error object but also
 *      // has fields like networkOK and status
 *   } )
 * ```
 * @type Promise
 */ 
class API {
    
    #loggedIn                        = false;
    
    #apiWrapper                      = null;
    
    #url                             = null;
    #fileUploadURL                   = null;
    #fileDownloadURL                 = null;

    #store                           = null;

    #useMFA                          = false;

    /**
     * Constructor which includes the logCallback
     * @param {function} logCallback A callback for all calls. Usefull to provide 
     * global logging
     * @returns {API}
     */
    constructor(logCallback=null, url = null, fileUploadURL = null, fileDownloadURL = null){

        
        this.#store = ((typeof window) !== 'undefined')? 
        window.sessionStorage: 
        ({ getItem: () => "Item", setItem: (k,v)=>null });

        this.#url                       = url;
        this.#fileUploadURL             = fileUploadURL;
        this.#fileDownloadURL           = fileDownloadURL;
        this.#apiWrapper                = new APICallWrapper();
        this.#apiWrapper.token          = this.#store.getItem( 'apiToken' );
        if (logCallback) {
            this.#apiWrapper.addLogCallback(logCallback);
        }
    }
    
    /** 
     * @callback fetchFun
     * @param {string} messageID
     * @param {Object} params
     * @returns {Object}
    */
    /**
     * Sets an alternate function to fake a network connection. Easier than
     * mocking window.fetch.
     * @param {fetchFun} networkFunction 
     */
    setAlternateNetworkConnectionFunction(networkFunction) {
        this.#apiWrapper.setFetch(networkFunction);
    }

    /**
     * If set, logins using the login function will use MFA. 
     * @param {boolean} useMFA
     * @returns {undefined}
     * @see login
     */
    setUseMFA(useMFA) {
        this.#useMFA = useMFA;
    }

    /**
     * The log callback is used to catch all API calls so they can be displayed 
     * or logged for debugging.
     * @param {type} logCallback
     * @returns {undefined}
     */
    setLogCallback( logCallback ){
        this.#apiWrapper.removeLogCallback( logCallback );
        this.#apiWrapper.addLogCallback( logCallback );
    }

    addLogCallback(logCallback) {
        this.#apiWrapper.addLogCallback(logCallback);
    }

    removeLogCallback(logCallback) {
        this.#apiWrapper.removeLogCallback(logCallback);
    }
    
    /**
     * Logs in. Behind the scenes this stores the API token in browser session 
     * store.
     * NB. Refactor required as currently the api object is passed arround, to 
     * hold the token but then the token is always retreved from storage anyway.
     * @param {string} username
     * @param {string} password
     * @param {string} mfaToken
     * @returns {Promise}
     * @see setUseMFA
     */
    login(username, password, mfaToken = null){


        const params = {   
            username, 
            password };
        if(mfaToken){
            params.mfaToken = ''+mfaToken;
        }
        if(typeof this.#useMFA !== 'undefined'){
            params.UseMFA = this.#useMFA;
        }

        return this.#apiWrapper.transact( 'Authenticate', 
            HTTP_POST, 
            this.#url, 
            {...params})
            .then(r => r.apiResult)
            .then(r => {
                this.loggedIn               = true;
                this.#apiWrapper.token      = r;
                
                this.#store.setItem( 'apiToken', this.#apiWrapper.token );

                return Promise.resolve(this.#apiWrapper.token);
            })
            .catch(err =>{
                this.loggedIn               = false;
                this.#apiWrapper.token      = null;

                this.#store.setItem( 'apiToken', null );

                return Promise.reject(err);
            });
    }

    /**
     * Validates the currently stored token or the one supplied. When a new 
     * token is supplied, the existing stored one is removed. If a supplied
     * token validates then it becomes the new stored token. This call can
     * therefore be used to login with a supplied token.
     * This call cannot be used to attempt to login with a new token
     * and fall back to an existing one. 
     * @param {type} token If supplied, the currently stored token is removed
     * and the supplied token is validated. Otherwise, just validate the 
     * currently held token.
     * @returns {Promise} A promise that resolves OK if the current token or 
     * supplied token validate.
     */
    validateLoggedIn(token = null) {

        /* if we are validating a token supplied from elsewhere then we clear
         * the currently stored token. We do not currently manage multiple 
         * tokens at a time. If the token validates then it will be set as the 
         * new token for further transactions */
        if (token) {
            this.#apiWrapper.token = null
            this.#store.setItem('apiToken', null)
        }

        return this.#apiWrapper.transact('ValidateToken',
            HTTP_POST,
            this.#url,
            { Token: token ? ''+token : ''+this.#apiWrapper.token })
            .then(r => r.apiResult)
            .then(response => {
                this.loggedIn = true
                this.#apiWrapper.token = ''+response
                this.#store.setItem('apiToken', ''+response);
                return Promise.resolve(token);
            })
            .catch(e => {

                this.loggedIn = false;
                this.#apiWrapper.token = null;

                this.#store.setItem('apiToken', this.#apiWrapper.token);
                return Promise.reject(e);
            });
    }

    getToken() {
        return this.#apiWrapper.token;
    }

    /**
     * Used to set a token that has already been validated, for example by being
     * returned from getBookingSiteConfig which returns a valid token.
     * @param {type} token
     * @returns {undefined}
     */
    setToken( token ){
        this.#apiWrapper.token = token;
        this.#store.setItem( 'apiToken', this.#apiWrapper.token );
    }
    
    isLoggedIn(){
        return null !== this.#apiWrapper.token
    }
    
    logout(){
        
        this.#apiWrapper.token = null;
        this.#store.setItem( 'apiToken', null )
    }
    
    /**
     * Makes a transaction with the API.
     * @param {string} messageID the messageID is determins the action performed
     * by the API.
     * @param {Object} params data to supply for the action
     * @returns {Promise} A promise to provide the result or an error
     */
    transact( messageID, params ){

        return this.#apiWrapper.transact( messageID, 
            HTTP_POST, 
            this.#url, 
            params 
        )
    }
    
     

    /* ---------------------------------------------------------------------
       -- functions below to be removed. Instead, will just use 
       -- transact so as to avoid this file having to be kept in sync 
       -- with hundreds of API messageIDs. Keeping for now
       -- to avoid a big update to Chronos
     */


    /**
     * Gets details about the person identified by partyID
     * @returns {Promise}
     */
    getPersonDetails( partyID ){
         return this.#apiWrapper.transact( 'PersonLoad', 
            HTTP_POST, 
            this.#url, 
            {
                partyid: partyID
            } 
        )
    }
    
    /**
     * Gets a list of appointment types
     * @returns {Promise} A promise that resolves to an apiResult which
     * is an array of appointment types
     */
    AppointmentTypeList(){
        
        return this.#apiWrapper.transact( 'AppointmentTypeList', 
            HTTP_POST, 
            this.#url, 
            {} 
        )
    }
    
    /**
     * Gets a list of injury regions "Abdomen/Lower trunk" or "Chest" for 
     * example. The diary booking system lets the booker choose up to three
     * injury regions and their appointment will be longer if there are more
     * regions selected.
     * @returns {Promise}
     */
    injuryRegionList(){
        return this.#apiWrapper.transact( 'InjuryRegionList', 
            HTTP_POST, 
            this.#url, 
            {} 
        )
    }
    
    
    /**
     * Gets available appointment slots for a team of clinicians and appointment 
     * type. 
     * @param {type} TeamID 
     * @param {type} appointmentType the ID from a call to getAppointmentTypes
     * @param {type} startTime  start of the range of time to search
     * @param {type} endTime    end of the range of time to search
     * @param {type} multiplier Effectlively books more than one appointment in 
     * one go. Generally this would be used if there are multiple injury regions
     * and therefore a longer appointment is required.
     * @returns {Promise}
     */
    getSlots(appointmentType, startTime, endTime, multiplier = 1){
         return this.#apiWrapper.transact( 'GetSlots', 
            HTTP_POST, 
            this.#url, 
            {AppointmentTypeID:      appointmentType,
            Multiplier:             multiplier,
            StartTime:              startTime,
            FinishTime:             endTime} 
        )
    }
    
    /**
     * The same as the getSlots call except this call is intended for frontend 
     * booking and so returns less data. It is also accessible with lower 
     * permision levels.
     * @param {type} appointmentType
     * @param {type} startTime
     * @param {type} endTime
     * @param {type} multiplier
     * @returns {Function}
     */
    getBookingSlots(appointmentType, startTime, endTime, multiplier = 1){
         return this.#apiWrapper.transact( 'GetBookingSlots', 
            HTTP_POST, 
            this.#url, 
            {AppointmentTypeID:      appointmentType,
            Multiplier:             multiplier,
            StartTime:              startTime,
            FinishTime:             endTime} 
        )
    }
    
    /**
     * @deprecated
     * @param {integer} TeamID
     * @param {string} slotLength
     * @param {string} startTime
     * @param {string} endTime
     * @param {integer} multiplier
     * @returns {Promise} A promise which gets an object with a apiResult which
     * is an array of slots
     */
    getAll(TeamID, slotLength, startTime, endTime, multiplier = 1){
        return this.#apiWrapper.transact( 'GetAll', 
            HTTP_POST, 
            this.#url, 
            {TeamID:                TeamID,
            SlotLength:             slotLength,
            Multiplier:             multiplier,
            StartTime:              startTime,
            FinishTime:             endTime} 
        )
    }
    
    /**
     * Gets the diary events for a team. This includes available periods and 
     * clinical appointments. 
     * @param {integer} TeamID
     * @param {string} startTime
     * @param {string} endTime
     * @returns {Promise} A promise which gets an object with a apiResult which
     * is an array of available periods.
     * The fields in the result include 
     */
    getTeamDiary(TeamID, startTime, endTime){
         return this.#apiWrapper.transact( 'GetTeamDiary', 
                        HTTP_POST, 
                        this.#url, 
                        {TeamID: TeamID,
                        StartTime: startTime,
                        FinishTime: endTime} 
        )
    }
    
 
    /**
     * ```
     * @returns {Promise} 
     */
    getTeamMemberList(teamID){
        if(!teamID) return doReject( 'TeamMemberList: No team ID value')
         return this.#apiWrapper.transact( 'TeamMemberList', 
                        HTTP_POST, 
                        this.#url, 
                        {TeamID: teamID} 
        )
    }
    
    /**
     * Gets a list of teams
     * @returns {Function}
     */
    getTeamList(){
        
        return this.#apiWrapper.transact( 'TeamList', 
                        HTTP_POST, 
                        this.#url, 
                        {} 
        )
    }
    
    createTeam(teamName){
        return this.#apiWrapper.transact( 'SaveTeam', 
            HTTP_POST, 
            this.#url, 
            {TeamName: teamName} 
        )
    }
    
    /**
     * Lists people who are setup to be in diary teams. These people can have
     * diaries.
     */
    listDiaryUsers(){
        
         return this.#apiWrapper.transact(    'DiaryUserList', 
                                    HTTP_POST, 
                                    this.#url, 
                                    {} 
        )
    }
    
    addMemberToTeam(partyID, teamID){
        return this.#apiWrapper.transact(    'SaveTeamMember', 
                                    HTTP_POST, 
                                    this.#url, 
                                    {
                                        PartyID:    partyID,
                                        TeamID:     teamID
                                    } 
        )
    }
    
    removeMemberFromTeam(partyID, teamID){
        return this.#apiWrapper.transact(    'RemoveTeamMember', 
                                    HTTP_POST, 
                                    this.#url, 
                                    {
                                        PartyID:    partyID,
                                        TeamID:     teamID
                                    } 
        )
    }
    
    /**
     * Similar to get diary but only returns available time, not appointments 
     * @param {type} PartyID
     * @param {type} startTime
     * @param {type} endTime
     * @returns {Function}
     */
    getRota(PartyID, startTime, endTime){
        return this.#apiWrapper.transact( 'GetRota', 
            HTTP_POST, 
            this.#url, 
            {PartyID: PartyID,
            StartTime: startTime,
            FinishTime: endTime} 
        )
    }
    
    /** 
     * Create a new timer period in a diary.
     * @param {type} PartyID    The clinician who's diary is changed
     * @param {type} startTime  The start of the time period
     * @param {type} endTime    The end of the time period
     * @param {type} rotaTypeID The type of entry. These can be discovered with
     *                          getRotaTypeList
     * @returns {Promise}
     */
    createRota(PartyID, startTime, endTime, description,
        rotaTypeID, available = false){
        return this.#apiWrapper.transact( 'SaveRota', 
            HTTP_POST, 
            this.#url, 
            {PartyID:                   PartyID,
            RotaTypeID:                 rotaTypeID,
            StartTime:                  startTime,
            FinishTime:                 endTime,
            Description:                description,
            Available:                  available} 
        )
    }
    
    /**
     * Updated the rota. 
     * @param {type} rotaID     The ID that came back from createRota
     * @param {type} startTime  The start of the time period
     * @param {type} endTime    The end of the time period
     * @param {type} rotaTypeID The type of entry. These can be discovered with
     *                          getRotaTypeList
     * @returns {Function}
     */
    updateRota(rotaID, startTime, endTime, description, partyID = 0, rotaTypeID, 
        available = false){
        
        if(!rotaID){
            throw new Error('RotaID missing');
        }
        
        return this.#apiWrapper.transact( 'SaveRota', 
            HTTP_POST, 
            this.#url, 
            {RotaID:                    rotaID,
            RotaTypeID:                 rotaTypeID,
            PartyID:                    partyID,
            StartTime:                  startTime,
            FinishTime:                 endTime,
            Description:                description,
            Available:                  available} 
        )
    }
    
    
    /** 
     * Create a new timer period in a diary.
     * @param {type} PartyID    The clinician who's diary is changed
     * @param {type} startTime  The start of the time period
     * @param {type} endTime    The end of the time period
     * @param {type} rotaTypeID The type of entry. These can be discovered with
     *                          getRotaTypeList
     * @returns {Promise}
     */
    createTask(PartyID, startTime, endTime, description,
        rotaTypeID, available = false, title = ''){
        return this.#apiWrapper.transact( 'SaveDiaryTask', 
            HTTP_POST, 
            this.#url, 
            {PartyID:                   PartyID,
            RotaTypeID:                 rotaTypeID,
            StartTime:                  startTime,
            FinishTime:                 endTime,
            Description:                description,
            Available:                  available,
            Title:                      title} 
        )
    }
    
    /**
     * Updated the rota. 
     * @param {type} rotaID     The ID that came back from createRota
     * @param {type} startTime  The start of the time period
     * @param {type} endTime    The end of the time period
     * @param {type} rotaTypeID The type of entry. These can be discovered with
     *                          getRotaTypeList
     * @returns {Function}
     */
    updateTask(rotaID, startTime, endTime, description, partyID = 0, rotaTypeID, 
        available = false, title = ''){
        
        if(!rotaID){
            throw new Error('RotaID missing');
        }
        
        return this.#apiWrapper.transact( 'SaveDiaryTask', 
            HTTP_POST, 
            this.#url, 
            {RotaID:                    rotaID,
            RotaTypeID:                 rotaTypeID,
            PartyID:                    partyID,
            StartTime:                  startTime,
            FinishTime:                 endTime,
            Description:                description,
            Available:                  available,
            Title:                      title} 
        )
    }
    
     /** 
     * Deletes a TimePeriod from the diary. This may be available time or lunch
     * etc. 
     * @param {integer} rotaID the ID returned from getTeamDiary or similar call
     * @returns {Promise}
     */
    deleteTask(rotaID){
        if( !rotaID ){
            throw 'Rota ID must be provided';
        }
        return this.#apiWrapper.transact( 'DeleteDiaryTask', 
            HTTP_POST, 
            this.#url, 
            {RotaID:                    rotaID} 
        )
    }
    
    /**
     * Sets the appointmentTypes for a list of 
     * @param {type} rotaID
     * @param {Array} appointmentTypes An array of appointmentTypeIDs
     * @returns {Function}
     */
    saveRotaAppointmentType(rotaID, appointmentTypes){
        return this.#apiWrapper.transact( 'SaveRotaAppointmentType', 
            HTTP_POST, 
            this.#url, 
            {RotaID:                    rotaID,
            AppointmentTypeList:        appointmentTypes} 
        )
    }
    
    /**
     * Gets the available appointment types for a clinician.
     * @param {type} partyID
     * @param {type} startTime
     * @param {type} finishTime
     * @returns {Promise}
     */
    GetPartyTimeAppointmentTypeList(partyID, startTime, finishTime){
        return this.#apiWrapper.transact( 'GetPartyTimeAppointmentTypeList', 
            HTTP_POST, 
            this.#url, 
            {PartyID:                   partyID,
            StartTime:                  startTime,
            FinishTime:                 finishTime  } 
        )
    }
    
    /**
     * 
     * @param {type} rotaID
     * @returns {Function}
     */
    getRotaAppointmentTypeList( rotaID ){
        return this.#apiWrapper.transact( 'GetRotaAppointmentType', 
            HTTP_POST, 
            this.#url, 
            {RotaID:                    rotaID} 
        )
    }
    
    /** 
     * Deletes a TimePeriod from the diary. This may be available time or lunch
     * etc. 
     * @param {integer} rotaID the ID returned from getTeamDiary or similar call
     * @returns {Promise}
     */
    deleteRota(rotaID){
        return this.#apiWrapper.transact( 'DeleteRota', 
            HTTP_POST, 
            this.#url, 
            {RotaID:                    rotaID} 
        )
    }
    
    /** 
     * Gets a list of rota types which can represent things such as lunch or
     * available. Things that can be entered to mark someone as available to take
     * bookings.
     * @returns {Function}
     */
    getRotaTypeList(){
        return this.#apiWrapper.transact( 'GetRotaTypeList', 
            HTTP_POST, 
            this.#url, 
            {} 
        )
    }
    
    /**
     * Returns diary information regarding a single user. Shows available time 
     * from the Rota and appointments booked through the diary.
     * {partyID} integer The diary user to search for
     * {startTime}: Search from this time
     * {finishTime}: Search to this time
     */
    getDiary(partyID, startTime, finishTime){
        return this.#apiWrapper.transact( 'GetDiary', 
            HTTP_POST, 
            this.#url, 
            {PartyID: partyID,
            StartTime: startTime,
            FinishTime: finishTime} 
        )
    }
    
    /**
     * Identical to holdSlot but returns less detail data and is acessible 
     * with lower permissions
     * @param {type} appointmentType
     * @param {type} startTime
     * @param {type} finishTime
     * @param {type} multiplier
     * @returns {Function}Almost 
     */
    holdBookingSlot(appointmentType, startTime, finishTime, multiplier = 0 ){
        const params = {
            AppointmentTypeID:   appointmentType,
            StartTime:           startTime,
            FinishTime:          finishTime
        };
        if(multiplier){
            params.Multiplier = multiplier;
        }
        return this.#apiWrapper.transact( 'HoldBookingSlot', 
            HTTP_POST, 
            this.#url, 
            params
        )
    }
    
    
    /* Finalises an appointment booking that has previously been held with 
     * holdSlot.
     * Similar to finaliseSlot, but returns less data */
    finaliseBookingSlot(bookingID, caseID, description){
        
        return this.#apiWrapper.transact( 'FinaliseBookingSlot', 
            HTTP_POST, 
            this.#url, 
            {
                BookingID:          bookingID,
                CaseID:             caseID,
                Description:        description
            }
        )
    }
    
    /**
     * 
     * @param {type} appointmentType
     * @param {type} startTime
     * @param {type} multiplier
     * @param {Date} finishTime
     * @returns {Object} The booking ID will be in 'apiResult.BookingID' if the 
     * booking was made.
     */
    holdSlot(appointmentType, startTime, finishTime = null, multiplier = 0 ){
        const params = {
            AppointmentTypeID:   appointmentType,
            StartTime:          startTime
        };
        if(finishTime){
            params.FinishTime = finishTime;
        }
        if(multiplier){
            params.Multiplier = multiplier;
        }
        return this.#apiWrapper.transact( 'HoldSlot', 
            HTTP_POST, 
            this.#url, 
            params
        )
    }
    
    /**
     * This is similar to holdSlot except it takes a partyID. In this case the 
     * backend will be looking for times for this party and won't try to find a
     * new party.
     * @param {type} appointmentType
     * @param {type} partyID
     * @param {type} startTime
     * @param {Date} finishTime
     * @returns {Object} The booking ID will be in 'apiResult.BookingID' if the 
     * booking was made.
     */
    holdPartySlot(appointmentType, partyID, startTime, finishTime ){
        const params = {
            AppointmentTypeID:   appointmentType,
            StartTime:           startTime,
            FinishTime:          finishTime,
            PartyID:             partyID
        };
        return this.#apiWrapper.transact( 'HoldSlot', 
            HTTP_POST, 
            this.#url, 
            params
        )
    }
    
    /* Finalises an appointment booking that has previously been held with 
     * holdSlot */
    finaliseSlot(bookingID, caseID, description){
        
        return this.#apiWrapper.transact( 'FinaliseSlot', 
            HTTP_POST, 
            this.#url, 
            {
                BookingID:          bookingID,
                CaseID:             caseID,
                Description:        null!==description? description: ''
            }
        )
    }
    
    makeBooking( caseID, partyID, start, end, appointmentTypeID, description ){
        return this.#apiWrapper.transact( 'SaveBooking', 
            HTTP_POST, 
            this.#url, 
            {
                CaseID:             caseID,
                PartyID:            partyID,
                StartTime:          start,
                FinishTime:         end,
                AppointmentTypeID:  appointmentTypeID,
                Description:        description,
                Confirmed:          true
            }
        )
    }
    
    /**
     * Reschedules a booking, using a held booking to researve the party and time.
     * @param {type} bookingID
     * @param {type} holdID
     * @returns {Function}
     */
    rescheduleBooking( bookingID, holdID ){
        return this.#apiWrapper.transact( 'RescheduleBooking', 
            HTTP_POST, 
            this.#url, 
            {
                BookingID:  bookingID,
                HoldID:     holdID
            }
        )
    }
    
    updateBookingDescription( bookingID, description ){
        return this.#apiWrapper.transact( 'UpdateBookingDescription', 
            HTTP_POST, 
            this.#url, 
            {
                BookingID:      bookingID,
                Description:    description
            }
        )
    }
    
    deleteBooking( bookingID ){
        return this.#apiWrapper.transact( 'DeleteBooking', 
            HTTP_POST, 
            this.#url, 
            {
                BookingID:  bookingID
            }
        )
    }
    
    getBooking( bookingID ){
        return this.#apiWrapper.transact( 'GetBooking', 
            HTTP_POST, 
            this.#url, 
            {
                BookingID:     bookingID
            }
        )
    }
    
    
    
    /**
     * Saves a patient referral. Patient data can include Salutation, Forename,
     * Surname, DOB, EmployeeNumber, PolicyNumber, Addr1, Addr2, Town, County,
     * Postcode, HomeTelNo, Email.   
     * @param {type} clientID
     * @param {type} serviceActivityId
     * @param {type} patientData an object with keys defained in the IPRS API
     * @returns {Function}
     */
    saveReferralForm(clientID, serviceActivityId, patientData){
        return this.#apiWrapper.transact( 'SaveReferralForm', 
            HTTP_POST, 
            this.#url, 
            {Data: {
                ClientId:           clientID,
		ServiceActivityId:  serviceActivityId,
                ...patientData
            }} 
        )
    }
    
    /** 
     * Get a list of rota templates. These are weekly templates which contain
     * psuedo events which have a day number and time. These can then be applied 
     * to weeks in a person's calendar */
    getRotaTemplateList(){
        return this.#apiWrapper.transact( 'GetRotaWeekTemplateList', 
            HTTP_POST, 
            this.#url, 
            {} 
        )
    }
    
    createTemplate(templateName){
        return this.#apiWrapper.transact( 'SaveRotaWeekTemplate', 
            HTTP_POST, 
            this.#url, 
            {
                TemplateName: templateName
            } 
        )
    }
    
    updateTemplate(id, templateName){
        return this.#apiWrapper.transact( 'SaveRotaWeekTemplate', 
            HTTP_POST, 
            this.#url, 
            {
                RotaWeekTemplateID: id,
                TemplateName: templateName
            } 
        )
    }
    
    deleteTemplate(id){
        return this.#apiWrapper.transact( 'DeleteRotaWeekTemplate', 
            HTTP_POST, 
            this.#url, 
            {
                RotaWeekTemplateID: id
            } 
        )
    }
    
    
    /**
     * Retreive a rota template, by ID. The ID can be obtained from 
     * getRotaTemplateList
     * @param {type} id
     * @returns {Promise}
     */
    getRotaTemplate(id){
        return this.#apiWrapper.transact( 'GetRotaTemplate', 
            HTTP_POST, 
            this.#url, 
            {RotaWeekTemplateID: id} 
        )
    }
    
    /**
     * Apply a template to a person's rota. 
     * @param {type} id
     * @param {type} partyID
     * @param {type} startTime
     * @param {type} finishTime
     * @returns {Promise}
     */
    applyRotaTemplate(id, partyID, startTime, finishTime){
        return this.#apiWrapper.transact( 'ApplyTemplate', 
            HTTP_POST, 
            this.#url, 
            {RotaWeekTemplateID: id,
            PartyID:             partyID,
            StartTime:           startTime,
            FinishTime:          finishTime} 
        )
    }
    
    /**
     * 
     * @param {type} templateID
     * @param {type} startTime
     * @param {type} endTime
     * @param {type} dayOfWeek
     * @param {type} rotaTypeID
     * @returns {Function}
     */
    createRotaTemplateEntry(templateID, startTime, endTime, dayOfWeek, rotaTypeID){
        return this.#apiWrapper.transact( 'SaveRotaWeekTemplateEntry', 
            HTTP_POST, 
            this.#url, 
            {RotaWeekTemplateID: templateID,
            DayOfWeek: dayOfWeek,
            TimeStart: startTime,
            TimeFinish: endTime,
            RotaTypeID: rotaTypeID
            } 
        )
    }
    
    /**
     * 
     * @param {type} id
     * @param {type} templateID
     * @param {type} startTime
     * @param {type} endTime
     * @param {type} dayOfWeek
     * @param {type} rotaTypeID
     * @param {type} rowVersion
     * @returns {Function}
     */
    updateRotaTemplateEntry(id, templateID, startTime, endTime, 
        dayOfWeek, rotaTypeID, rowVersion){
        
        return this.#apiWrapper.transact( 'SaveRotaWeekTemplateEntry', 
            HTTP_POST, 
            this.#url, 
            {RotaWeekTemplateEntryID: id,
            RotaWeekTemplateID: templateID,
            DayOfWeek: dayOfWeek,
            TimeStart: startTime,
            TimeFinish: endTime,
            RotaTypeID: rotaTypeID,
            RowVersion: rowVersion
            } 
        )
    }
    
    deleteRotaTemplateEntry(id, templateID){
        
        return this.#apiWrapper.transact( 'DeleteRotaWeekTemplateEntry', 
            HTTP_POST, 
            this.#url, 
            {
                RotaWeekTemplateID: templateID,
                RotaWeekTemplateEntryID: id 
            } 
        )
    }
    
    SaveRotaWeekTemplateEntryAppointmentType( id, appointmentTypeList ){
        return this.#apiWrapper.transact( 'SaveRotaWeekTemplateEntryAppointmentType', 
            HTTP_POST, 
            this.#url, 
            {
                RotaWeekTemplateEntryID: id,
                AppointmentTypeList: appointmentTypeList
            } 
        )
    }
    
    getRotaWeekTemplateEntryAppointmentTypeList( id ) {
        return this.#apiWrapper.transact( 'GetRotaWeekTemplateEntryAppointmentTypeList', 
            HTTP_POST, 
            this.#url, 
            {
                RotaWeekTemplateEntryID: id
            } 
        )
    }
    
    /**
     * Currently this uses the internal caseID
     * @param {type} caseID The internal case ID
     * @returns {Function}
     */
    getPatientOverview(caseID){
       return this.#apiWrapper.transact( 'PWPatientOverview', 
            HTTP_POST, 
            this.#url, 
            {
                CaseID: caseID
            } 
        )
        .then( r => { 
            r.apiResult.pkCaseID = r.requestParams.CaseID
            // retain the pkCaseID from the original request
            return r
        } )
    }
    
    getPatientOverviewExtID(extenalCaseID){
        
        return this.caseSearchExactMatch( extenalCaseID )
        .then( r => r.apiResult.pkCaseId )
        .then( pkCaseId => this.getPatientOverview( pkCaseId ) )
    }
    
    /**
     * Used to get the internal pkCaseID from an external caseID. Can also be 
     * used to get near matches to a caseID e.g. 49-001 will match 00093749-001
     * and 00094549-001.
     * @param {type} caseIDext The external CaseID 
     * @returns {Function}
     */
    caseSearch( caseIDext ){
        return this.#apiWrapper.transact( 'CaseSearch', 
            HTTP_POST, 
            this.#url, 
            {
                CaseID: caseIDext.trim()
            } 
        )
    }
    
    /**
     * Used to get the internal pkCaseID from an external caseID. This version
     * will only return exact matches.
     * @param {type} caseIDext The external CaseID 
     * @returns {Function}
     */
    caseSearchExactMatch( caseIDextNt ){
        
        const caseIDext = caseIDextNt.trim();
        
        return new Promise( 
            (resolve, reject) => {
                
                const err = (resp, msg = 'Not an exact match') => { return new APIError('CaseSearch', HTTP_POST, this.#url, resp.requestParams, msg) }
                
                this.caseSearch( caseIDext )
                .then( response => {
                    const apiResult = response.apiResult
                    
                    if( 1 === !apiResult.length ) {
                        reject( err(response) ) 
                    }
                    const record = apiResult[0]
                    if ( caseIDext !== record.CaseID ){
                        reject( err(response) ) 
                    }
                    const result = new APIResonse( 'CaseSearch', HTTP_POST, this.#url,
                        response.requestParams )
                    result.apiResult = record
                    resolve( result )
                } )
                .catch( error => reject( err( error, error.message ) ) )
            }    
        )
    }
    
    /** 
     * Lookup addresses for a postcode
     * ```
     * {
     *  "Address1": "Address1",
     *  "Address2": "Address2",
     *  "Town": "Town",
     *  "County": "County",
     *  "Postcode": "Postcode"
     * }
     * ```
     * @param {string} postcode The UK Postcode to be looked up. This may contain
     * a space but doesn't have to.
     * @returns {Promise} Promise object that represents the addresses returned
     * by the API 
     */
    addressLookup(postcode) {
       
       /* trim used as the webLayer / WebAPI will not work if there are trailing 
        * spaces. WebLayer converts this to a URL with the postcode at the end,
        * URL encoded and it seems the trailing %20 prevents a route match in 
        * the webAPI*/
       
       return this.#apiWrapper.transact( 'AddressLookup', 
            HTTP_POST, 
            this.#url, 
            {Postcode: postcode.trim()} 
        );
   } 
   
    /**
     * Gets the configuration for a booking website. The user will have been 
     * refered to the site and the REACT code might be used for more than one
     * domain. The URL the user visits may contain a token
     * and it may contain a reCaptchaToken. 
     * It is andatator to have either token or reCaptchaToken to get a token
     * in the response. If up
     * The API, is then able to return configuration based on any of these 
     * parameters.
     * @param {string}      token
     * @param {string}      reCaptchaToken
     * @param {string}      loc  The cannonical URL e.g 
     *                           https://iprsbooking.test/bookingsite/
     * @returns {Promise}
     */
    getBookingSiteConfig(token, reCaptchaToken, loc){
        
        return this.#apiWrapper.transact( 'GetBookingSiteConfig', 
            HTTP_POST, 
            this.#url, 
            {
                Token:          token,
                ReCaptchaToken: reCaptchaToken,
                URL:            loc
            } 
        );
    }
      
   /**
    * Takes an array of File objects and uploads them to the Web API. The files
    * could have come from an upload form or a Drag and Drop.
    * @param {Array}    files an Array of File objects
    * @param {int}      caseID An identifier for the patient case. This might 
    *                   have been returned from a call to saveReferralForm.
    * @param {int}      facilityID An identifier for the clinic, usually inputted from the clinic profile.
    * @param {int}      documentCategoryID An identifier for the document category.
    * @returns {Promise}
    */
   uploadFiles(files, caseID = 0, facilityID = 0, documentCategoryID = 0){
       
       return this.#apiWrapper.uploadFiles(this.#fileUploadURL, files, caseID, facilityID, documentCategoryID);
    }
    
    /**
    * Takes a GUID and calls RetrieveFile from the Web API.
    * @param {GUID}      GUID the GUID assigned to a file when it stored in blob storage.
    * @returns {Promise}
    */
   downloadFile(GUID){
       
       return this.#apiWrapper.downloadFile(this.#fileDownloadURL, GUID);
    }
    
    }
    
export default API;